import { useState, useEffect } from "react";
import AdminLayout from "../components/AdminLayout";

interface MessageCenterSettings {
  apiKey: string;
}

const MessageCenterSettingsPage = () => {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch("/backend/api/get_site_content.php");
      const result = await response.json();
      
      if (result.success && result.data.messageCenter) {
        setApiKey(result.data.messageCenter.apiKey || "");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      setMessage({ type: "error", text: "Error al cargar la configuración" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Primero obtener todo el contenido actual
      const getResponse = await fetch("/backend/api/get_site_content.php");
      const getResult = await getResponse.json();

      if (!getResult.success) {
        throw new Error("Error al obtener el contenido actual");
      }

      // Actualizar solo el messageCenter
      const updatedContent = {
        ...getResult.data,
        messageCenter: {
          apiKey: apiKey.trim()
        }
      };

      // Guardar el contenido actualizado
      const saveResponse = await fetch("/backend/api/save_site_content.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedContent),
      });

      const saveResult = await saveResponse.json();

      if (saveResult.success) {
        setMessage({ type: "success", text: "✔ API Key guardada correctamente" });
      } else {
        throw new Error(saveResult.message || "Error al guardar");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setMessage({ type: "error", text: "Error al guardar la configuración" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Cargando configuración...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Configuración del Message Center
          </h1>
          <p className="text-gray-600">
            Configura la API Key para conectar el formulario de contacto con el AxloWeb Message Center
          </p>
        </div>

        {/* Estado de la API Key */}
        <div className="mb-6">
          {apiKey ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
              <div>
                <h3 className="font-semibold text-green-900 mb-1">
                  API Key configurada correctamente
                </h3>
                <p className="text-sm text-green-700">
                  El formulario de contacto está conectado al Message Center
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <i className="ri-alert-fill text-yellow-600 text-xl"></i>
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">
                  No se ha configurado la API Key
                </h3>
                <p className="text-sm text-yellow-700">
                  El formulario de contacto no podrá enviar mensajes hasta que configures la API Key
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Formulario de configuración */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-2">
              API Key del AxloWeb Message Center
            </label>
            <input
              type="text"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Pega aquí tu API Key"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <p className="mt-2 text-sm text-gray-500">
              Obtén tu API Key desde el panel de administración del AxloWeb Message Center
            </p>
          </div>

          {/* Mensaje de estado */}
          {message && (
            <div
              className={`mb-4 p-4 rounded-lg ${
                message.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Botón guardar */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium whitespace-nowrap cursor-pointer"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-save-line"></i>
                Guardar Cambios
              </span>
            )}
          </button>
        </div>

        {/* Información adicional */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <i className="ri-information-line"></i>
            ¿Cómo obtener tu API Key?
          </h3>
          <ol className="text-sm text-blue-800 space-y-1 ml-6 list-decimal">
            <li>Accede al panel de administración del AxloWeb Message Center</li>
            <li>Ve a la sección de "Configuración" o "API Keys"</li>
            <li>Copia tu API Key única</li>
            <li>Pégala en el campo de arriba y guarda los cambios</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MessageCenterSettingsPage;
